import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'platform/utilities/data/debounce';
import localStorage from 'platform/utilities/storage/localStorage';
import { toggleValues } from 'platform/site-wide/feature-toggles/selectors';
import FEATURE_FLAG_NAMES from 'platform/utilities/feature-toggles/featureFlagNames';
import { updateLayoutHeaderType } from 'platform/site-wide/layout/actions';
import { isLoggedIn } from 'platform/user/selectors';
import { useSelector, useDispatch } from 'react-redux';
import { Header as MobileHeader } from '../Header';
import HeaderNavigation from '../HeaderNavigation';
import {
  hideDesktopHeader,
  showDesktopHeader,
  toggleMinimalHeader,
} from '../../helpers';
import { buildMenuData } from '../../helpers/menuData';

const MOBILE_BREAKPOINT_PX = 768;
const HEADER_NAVIGATION_CACHE_KEY = 'headerNavigationV2';

function determineHeaderState(show, showMinimalHeader, path, isDesktop) {
  if (!show) {
    return 'none';
  }
  if (
    showMinimalHeader &&
    (typeof showMinimalHeader === 'function'
      ? showMinimalHeader(path)
      : showMinimalHeader)
  ) {
    return 'minimal';
  }

  if (!isDesktop) {
    return 'mobile';
  }

  return 'default';
}

function setStaticHeaderDisplay(
  show,
  isMinimalHeaderApplicable,
  headerState,
  isDesktop,
) {
  if (show) {
    if (isMinimalHeaderApplicable) {
      toggleMinimalHeader(headerState === 'minimal');
    }
    if (isDesktop) {
      showDesktopHeader();
    } else {
      hideDesktopHeader();
    }
  }
  // else everything is already hidden by default
}

export const App = ({
  megaMenuData,
  show,
  showMegaMenu,
  showNavLogin,
  showMinimalHeader,
}) => {
  const dispatch = useDispatch();
  const path = useSelector(state => state?.navigation?.route?.path);
  const featureToggles = useSelector(state => toggleValues(state));
  const loggedIn = useSelector(isLoggedIn);
  // Until the toggles arrive, use the value this browser saw last visit, so a
  // returning user doesn't watch the old header swap out for the new one.
  const togglesLoaded = featureToggles.loading === false;
  const [cachedHeaderNavigation] = useState(
    () => localStorage.getItem(HEADER_NAVIGATION_CACHE_KEY) === 'true',
  );
  // The new header carries the new My VA's mobile nav and links to its
  // pages, so both roll out and back together on one flag.
  // Read by its snake_case name: vets-api camelizes VA as an acronym, so the
  // camelCase entry is myVABlueskyEnabled, not myVaBlueskyEnabled.
  const headerNavigationEnabled =
    !!featureToggles[FEATURE_FLAG_NAMES.myVaBlueskyEnabled];
  const useHeaderNavigation = togglesLoaded
    ? headerNavigationEnabled
    : cachedHeaderNavigation;
  const [headerState, setHeaderState] = useState(null);
  const [isDesktop, setIsDesktop] = useState(
    window.innerWidth >= MOBILE_BREAKPOINT_PX,
  );
  const [updatedMegaMenuData, setUpdatedMegaMenuData] = useState(megaMenuData);

  useEffect(() => {
    const deriveIsDesktop = () =>
      setIsDesktop(window.innerWidth >= MOBILE_BREAKPOINT_PX);

    const onResize = debounce(100, deriveIsDesktop);

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Only flag-on browsers store anything, so the flag-off path writes nothing.
  useEffect(() => {
    if (!togglesLoaded) return;
    if (headerNavigationEnabled) {
      localStorage.setItem(HEADER_NAVIGATION_CACHE_KEY, 'true');
    } else {
      localStorage.removeItem(HEADER_NAVIGATION_CACHE_KEY);
    }
  }, [togglesLoaded, headerNavigationEnabled]);

  useEffect(() => {
    setUpdatedMegaMenuData(
      buildMenuData(megaMenuData, {
        loggedIn,
        toggles: featureToggles,
        useHeaderNavigation,
      }),
    );
  }, [megaMenuData, featureToggles, useHeaderNavigation, loggedIn]);

  useEffect(() => {
    const newHeaderState = determineHeaderState(
      show,
      showMinimalHeader,
      path,
      isDesktop,
    );

    // Run on every change, including the toggle resolving after first render:
    // the static desktop header must never show while va-header-navigation
    // renders into this root, and a separate effect would leave a frame with
    // both painted.
    setStaticHeaderDisplay(
      show,
      showMinimalHeader,
      newHeaderState,
      isDesktop && !useHeaderNavigation,
    );
    if (headerState !== newHeaderState) {
      setHeaderState(newHeaderState);
      dispatch(updateLayoutHeaderType(newHeaderState));
    }
  }, [
    show,
    showMinimalHeader,
    path,
    isDesktop,
    dispatch,
    headerState,
    useHeaderNavigation,
  ]);

  if (
    useHeaderNavigation &&
    show &&
    (headerState === 'mobile' || headerState === 'default')
  ) {
    return (
      <HeaderNavigation
        megaMenuData={updatedMegaMenuData}
        showMegaMenu={showMegaMenu}
        showNavLogin={showNavLogin}
      />
    );
  }

  if (!show || headerState !== 'mobile') {
    return null;
  }

  return (
    <MobileHeader
      megaMenuData={updatedMegaMenuData}
      showMegaMenu={showMegaMenu}
      showNavLogin={showNavLogin}
    />
  );
};

App.propTypes = {
  megaMenuData: PropTypes.arrayOf(PropTypes.object).isRequired,
  show: PropTypes.bool.isRequired,
  showMegaMenu: PropTypes.bool.isRequired,
  showNavLogin: PropTypes.bool.isRequired,
  showMinimalHeader: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
};

export default App;
